import { useMemo, useState } from 'react';
import { Segmented, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { num, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

/** What both lists carry about the ticket. */
interface OnTicket {
  /* Autotask's company id; 0 is Autotask's own company, CMIT Solutions. */
  companyId: number | null;
  account: string | null;
  accountNumber: string | null;
  ticketNumber: string | null;
  link: string | null;
  ticketTitle: string | null;
  status: string | null;
  queue: string | null;
  issue: string | null;
  subIssue: string | null;
  resource: string | null;
  resourceId: number | null;
  resourceActive: boolean | null;
  hoursWorked: number;
}
interface Ticket extends OnTicket {
  id: number;
  created: string | null;
  completed: string | null;
  description: string | null;
  contract: string | null;
  priority: string | null;
}
interface Entry extends OnTicket {
  id: number;
  date: string | null;
  role: string | null;
  workType: string;
  billableHours: number;
  nonBillableHours: number;
  posted: boolean;
  summary: string | null;
  internalNotes: string | null;
}
interface Payload {
  from: string;
  to: string;
  tickets: Ticket[];
  items: Entry[];
  fetchedUtc: string;
}

type View = 'tickets' | 'entries';
type Accounts = 'all' | 'cmit' | 'fix';

const hrs = (v: number) => num(v, 2);
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—');

/* Letters and digits only, lower case — so "CMIT Solutions", "CMIT  Solutions"
   and "cmit-solutions" are the same account. */
const squash = (v: string | null) => (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
/* The ticket's account is CMIT Solutions itself: Autotask's own company
   (account 0), or an account named exactly that — so a client named
   "CMIT Solutions of …" is not swept in with it. */
const isCmit = (r: OnTicket) => r.companyId === 0 || squash(r.account) === 'cmitsolutions';
/* The ticket's account starts with FIX — "FIX-…", "FIX …" or "Fix…" alike. */
const isFix = (r: OnTicket) => /^\s*fix/i.test(r.account ?? '');
const inAccounts = (r: OnTicket, a: Accounts) => a === 'all' || (a === 'cmit' ? isCmit(r) : isFix(r));

function quick(k: 'week' | 'lastWeek' | 'month' | 'lastMonth'): { from: string; to: string } {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  if (k === 'week') return { from: toLocalDateString(monday), to: todayLocal() };
  if (k === 'lastWeek') {
    const s = new Date(monday);
    s.setDate(s.getDate() - 7);
    const e = new Date(monday);
    e.setDate(e.getDate() - 1);
    return { from: toLocalDateString(s), to: toLocalDateString(e) };
  }
  if (k === 'month') return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 1)), to: todayLocal() };
  return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 0)) };
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'week', label: 'This week' },
  { k: 'lastWeek', label: 'Last week' },
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
];

/** The ticket number, linked to the ticket in Autotask when there is a link. */
function TicketNumber({ r }: { r: OnTicket }) {
  if (!r.ticketNumber) return <>—</>;
  return r.link ? (
    <a href={r.link} target="_blank" rel="noreferrer">
      {r.ticketNumber}
    </a>
  ) : (
    <>{r.ticketNumber}</>
  );
}

/**
 * Reports › Misfits — the tickets for the days chosen (routes/reportsMisfits.ts).
 * Its point is finding work that landed in the wrong place, so the Accounts
 * switch narrows to tickets on the CMIT Solutions account or on the FIX
 * holding accounts in one click.
 *
 * Two lists from one read: TICKETS, every ticket created in the days chosen
 * whether or not anyone has logged time on it (the default, because a ticket
 * nobody worked is exactly the kind that goes unnoticed); and TIME ENTRIES,
 * every time entry on a ticket in those days. Kept until Refresh.
 */
export function MisfitsReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('month'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [view, setView] = useState<View>('tickets');
  const [accounts, setAccounts] = useState<Accounts>('all');
  /* An Autotask resource id, or '' for everyone. */
  const [technician, setTechnician] = useState('');
  const [search, setSearch] = useState('');
  const data = useHeldResource<Payload>(`/reports/misfits?${new URLSearchParams({ from, to })}`);
  const allTickets = data.data?.tickets ?? [];
  const allEntries = data.data?.items ?? [];
  const current: OnTicket[] = view === 'tickets' ? allTickets : allEntries;

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  const counts = useMemo(() => ({ all: current.length, cmit: current.filter(isCmit).length, fix: current.filter(isFix).length }), [current]);

  /* The technicians in the list shown — a ticket's primary resource, or the
     person who logged the time. Active first, deactivated marked. */
  const technicians = useMemo(() => {
    const byId = new Map<number, { name: string; active: boolean }>();
    for (const r of current) if (r.resourceId && r.resource) byId.set(r.resourceId, { name: r.resource, active: r.resourceActive !== false });
    return [...byId]
      .map(([id, t]) => ({ id: String(id), ...t }))
      .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
  }, [current]);

  const q = search.trim().toLowerCase();
  const matches = (values: (string | null)[]) => !q || values.some((v) => v && v.toLowerCase().includes(q));
  const keep = (r: OnTicket) => inAccounts(r, accounts) && (!technician || String(r.resourceId) === technician);

  const tickets = useMemo(
    () =>
      allTickets.filter(
        (t) => keep(t) && matches([t.account, t.ticketNumber, t.ticketTitle, t.description, t.contract, t.queue, t.status, t.resource, t.issue]),
      ),
    [allTickets, accounts, technician, q],
  );
  const entries = useMemo(
    () =>
      allEntries.filter(
        (e) =>
          keep(e) &&
          matches([e.account, e.accountNumber, e.ticketNumber, e.ticketTitle, e.resource, e.role, e.workType, e.queue, e.summary, e.internalNotes]),
      ),
    [allEntries, accounts, technician, q],
  );

  /* Totals for what is shown, so the tiles follow the filters. */
  const ticketTotals = {
    open: tickets.filter((t) => !t.completed).length,
    worked: tickets.filter((t) => t.hoursWorked > 0).length,
    hours: tickets.reduce((s, t) => s + t.hoursWorked, 0),
    accounts: new Set(tickets.map((t) => t.account).filter(Boolean)).size,
  };
  const entryTotals = {
    worked: entries.reduce((s, e) => s + e.hoursWorked, 0),
    billable: entries.reduce((s, e) => s + e.billableHours, 0),
    nonBillable: entries.reduce((s, e) => s + e.nonBillableHours, 0),
    tickets: new Set(entries.map((e) => e.ticketNumber).filter(Boolean)).size,
    accounts: new Set(entries.map((e) => e.account).filter(Boolean)).size,
    unposted: entries.filter((e) => !e.posted).length,
  };
  const shownCount = view === 'tickets' ? tickets.length : entries.length;

  const exportCsv = () =>
    view === 'tickets'
      ? downloadCsv(`misfits_tickets_${from}_to_${to}_${fileStamp()}.csv`, [
          [
            'Created', 'Completed', 'Ticket Number', 'Title', 'Description', 'Account', 'Account Number', 'Contract', 'Queue', 'Status',
            'Priority', 'Issue', 'Sub-issue', 'Primary Resource', 'Hours Logged',
          ],
          ...tickets.map((t) => [
            t.created, t.completed, t.ticketNumber, t.ticketTitle, t.description, t.account, t.accountNumber, t.contract, t.queue, t.status,
            t.priority, t.issue, t.subIssue, t.resource, t.hoursWorked,
          ]),
        ])
      : downloadCsv(`misfits_time_${from}_to_${to}_${fileStamp()}.csv`, [
          [
            'Date', 'Account Name', 'Account Number', 'Ticket Number', 'Ticket Title', 'Status', 'Queue', 'Issue', 'Sub-issue', 'Resource',
            'Role', 'Work Type', 'Hours Worked', 'Billable Hours', 'Non-Billable Hours', 'Posted', 'Summary Notes', 'Internal Notes',
          ],
          ...entries.map((e) => [
            e.date, e.account, e.accountNumber, e.ticketNumber, e.ticketTitle, e.status, e.queue, e.issue, e.subIssue, e.resource,
            e.role, e.workType, e.hoursWorked, e.billableHours, e.nonBillableHours, e.posted ? 'Yes' : 'No', e.summary, e.internalNotes,
          ]),
        ]);

  return (
    <>
      <PageHead
        title="Misfits"
        lede="The tickets for the days you choose — every ticket created, whether or not anyone logged time — and the time entered on tickets, so tickets on CMIT Solutions or a FIX account are easy to find. Read live from Autotask."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="From">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((k) => (
                <button key={k.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(k.k))}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Show">
              <Segmented
                label="Show"
                value={view}
                onChange={(v) => {
                  setView(v);
                  setTechnician('');
                }}
                options={[
                  { id: 'tickets', label: `Tickets (${num(allTickets.length)})` },
                  { id: 'entries', label: `Time entries (${num(allEntries.length)})` },
                ]}
              />
            </Field>
            <Field label="Accounts">
              <Segmented
                label="Accounts"
                value={accounts}
                onChange={setAccounts}
                options={[
                  { id: 'all', label: `All (${num(counts.all)})` },
                  { id: 'cmit', label: `CMIT Solutions (${num(counts.cmit)})` },
                  { id: 'fix', label: `FIX (${num(counts.fix)})` },
                ]}
              />
            </Field>
            <Field label={view === 'tickets' ? 'Primary resource' : 'Technician'}>
              <select className="select" value={technician} onChange={(e) => setTechnician(e.target.value)}>
                <option value="">Everyone</option>
                {technicians.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.active ? t.name : `${t.name} (inactive)`}
                  </option>
                ))}
                {technician && !technicians.some((t) => t.id === technician) && <option value={technician}>Not in these days</option>}
              </select>
            </Field>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ticket, title, account, notes" />
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!shownCount} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            {view === 'tickets'
              ? `Tickets created ${day(from)} – ${day(to)}, open and closed, with the hours logged on each in those days.`
              : `Time entries on tickets dated ${day(from)} – ${day(to)}, posted and not.`}{' '}
            CMIT Solutions is a ticket whose account is named exactly that; FIX is a ticket whose account name begins with FIX.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !data.data && <Loading label="Reading tickets from Autotask" />}

      {data.data &&
        (view === 'tickets' ? (
          <>
            <div className="grid grid-4 mb-4">
              <Stat label="Tickets" value={num(tickets.length)} foot={`${num(ticketTotals.open)} still open`} />
              <Stat label="With time logged" value={num(ticketTotals.worked)} foot={`${num(tickets.length - ticketTotals.worked)} with none`} />
              <Stat label="Hours logged" value={hrs(ticketTotals.hours)} foot="on these tickets, in these days" />
              <Stat label="Accounts" value={num(ticketTotals.accounts)} foot="the tickets are on" />
            </div>

            {tickets.length === 0 ? (
              <section className="card">
                <div className="card-body">
                  <p className="muted small">No tickets {accounts !== 'all' || technician || q ? 'match' : 'were created in these days'}.</p>
                </div>
              </section>
            ) : (
              <section className="card">
                <div className="table-wrap">
                  {/* Laid out like Autotask's own ticket list: number and title, the
                      description, account and contract, queue and status, the resource. */}
                  <table className="table pp-table">
                    <colgroup>
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '26%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '7%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Created</th>
                        <th>Ticket</th>
                        <th>Description</th>
                        <th>Account / contract</th>
                        <th>Queue / status</th>
                        <th>Resource</th>
                        <th className="num">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t) => (
                        <tr key={t.id}>
                          <td className="xs">
                            {day(t.created)}
                            {t.completed && <div className="muted">done {day(t.completed)}</div>}
                          </td>
                          <td>
                            <div className="xs">
                              <TicketNumber r={t} />
                            </div>
                            {t.ticketTitle ?? '—'}
                            {(t.issue || t.subIssue) && <div className="xs muted">{[t.issue, t.subIssue].filter(Boolean).join(' › ')}</div>}
                          </td>
                          <td className="xs">
                            {t.description ? (
                              <span className="pp-note" title={t.description}>
                                {t.description}
                              </span>
                            ) : (
                              <span className="subtle">—</span>
                            )}
                          </td>
                          <td>
                            {t.account ?? '—'}
                            {t.contract && <div className="xs muted">{t.contract}</div>}
                          </td>
                          <td className="xs">
                            {t.queue ?? '—'}
                            {(t.status || t.priority) && <div className="muted">{[t.status, t.priority].filter(Boolean).join(' · ')}</div>}
                          </td>
                          <td className="xs">
                            {t.resource ?? <span className="subtle">Unassigned</span>}
                            {t.resourceActive === false && <span className="subtle"> (inactive)</span>}
                          </td>
                          <td className="num">{t.hoursWorked ? hrs(t.hoursWorked) : <span className="subtle">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-4 mb-4">
              <Stat label="Time entries" value={num(entries.length)} foot={`${num(entryTotals.unposted)} not yet posted`} />
              <Stat
                label="Hours worked"
                value={hrs(entryTotals.worked)}
                foot={`${hrs(entryTotals.billable)} billable · ${hrs(entryTotals.nonBillable)} non-billable`}
              />
              <Stat label="Tickets" value={num(entryTotals.tickets)} foot="with time in these days" />
              <Stat label="Accounts" value={num(entryTotals.accounts)} foot="the time was entered on" />
            </div>

            {entries.length === 0 ? (
              <section className="card">
                <div className="card-body">
                  <p className="muted small">No ticket time {accounts !== 'all' || technician || q ? 'matches' : 'was entered in these days'}.</p>
                </div>
              </section>
            ) : (
              <section className="card">
                <div className="table-wrap">
                  {/* Seven columns fixed to the page width, like Pre-posting: the account number
                      under the account, status under the ticket, the role under the technician. */}
                  <table className="table pp-table">
                    <colgroup>
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '25%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Account</th>
                        <th>Ticket</th>
                        <th>Technician</th>
                        <th>Work type</th>
                        <th className="num">Hours</th>
                        <th>Summary note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e) => (
                        <tr key={e.id}>
                          <td className="xs">
                            {day(e.date)}
                            {!e.posted && <div className="muted">not posted</div>}
                          </td>
                          <td>
                            {e.account ?? '—'}
                            {e.accountNumber && <div className="xs muted mono">{e.accountNumber}</div>}
                          </td>
                          <td>
                            <div className="xs">
                              <TicketNumber r={e} />
                            </div>
                            {e.ticketTitle}
                            {(e.status || e.queue) && <div className="xs muted">{[e.status, e.queue].filter(Boolean).join(' · ')}</div>}
                            {(e.issue || e.subIssue) && <div className="xs muted">{[e.issue, e.subIssue].filter(Boolean).join(' › ')}</div>}
                          </td>
                          <td className="xs">
                            {e.resource ?? '—'}
                            {e.resourceActive === false && <span className="subtle"> (inactive)</span>}
                            {e.role && <div className="muted">{e.role}</div>}
                          </td>
                          <td className="xs">{e.workType}</td>
                          <td className="num">
                            {hrs(e.hoursWorked)}
                            <div className="xs muted">
                              {hrs(e.billableHours)} bill · {hrs(e.nonBillableHours)} non
                            </div>
                          </td>
                          <td className="xs">
                            {e.summary ? (
                              <span className="pp-note" title={e.summary}>
                                {e.summary}
                              </span>
                            ) : (
                              <span className="subtle">—</span>
                            )}
                            {e.internalNotes && (
                              <div className="muted pp-note" title={e.internalNotes}>
                                Internal: {e.internalNotes}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        ))}
    </>
  );
}
